import type { Currency } from '@/shared/api/types'

/**
 * Раскладка «валюта + сеть», как в макете.
 *
 * В API валюта одна — `usdt_trc20`, `usdt_ton`, `btc` и так далее.
 * В макете это ДВА поля: «Валюта» и «Сеть». Расхождения тут нет,
 * а есть правильная мысль дизайнера: USDT живёт в двух сетях, адреса
 * несовместимы, и перевод в чужую сеть не возвращается никак. Спрятать
 * этот выбор в одну строку «USDT (TRC-20)» значит дать плательщику
 * промахнуться одним кликом.
 *
 * Поэтому фронт разворачивает плоский список API в два поля и потом
 * складывает обратно.
 */

export interface CoinInfo {
  /** Тикер в поле «Валюта». */
  ticker: string
  /** Полное имя монеты — для подписи в списке. */
  label: string
  /** Цвет метки. Только оформление. */
  accent: string
  /** Сети, в которых эта монета доступна. Ключ — `currency` из API. */
  networks: { currency: Currency; name: string }[]
}

const COIN_ORDER = ['BTC', 'LTC', 'XMR', 'GRAM', 'USDT'] as const

const COINS: Record<string, CoinInfo> = {
  BTC: {
    ticker: 'BTC', label: 'Bitcoin', accent: '#F7931A',
    networks: [{ currency: 'btc', name: 'Bitcoin' }],
  },
  LTC: {
    ticker: 'LTC', label: 'Litecoin', accent: '#345D9D',
    networks: [{ currency: 'ltc', name: 'Litecoin' }],
  },
  XMR: {
    ticker: 'XMR', label: 'Monero', accent: '#FF6600',
    networks: [{ currency: 'xmr', name: 'Monero' }],
  },
  /*
    Тикер GRAM, а не TON, и это не опечатка.

    В API валюта называется `gram`, и в макете она подписана «GRAM»
    во всех 44 местах, где встречается пара «валюта (сеть)»: «GRAM (TON)».
    TON здесь — СЕТЬ, в которой живут и GRAM, и USDT-TON. Назвать
    тикером тоже TON значит получить «TON (TON)» в истории рядом
    с «USDT (TON)» — и мерчант, выбирая кошелёк для автовывода,
    перестаёт понимать, где монета, а где сеть.
  */
  GRAM: {
    ticker: 'GRAM', label: 'Toncoin', accent: '#0098EA',
    networks: [{ currency: 'gram', name: 'TON' }],
  },
  USDT: {
    ticker: 'USDT', label: 'Tether', accent: '#26A17B',
    networks: [
      { currency: 'usdt_trc20', name: 'TRC20' },
      { currency: 'usdt_ton', name: 'TON' },
    ],
  },
}

/** Какой монете принадлежит валюта API. */
export function coinOf(currency: Currency): CoinInfo {
  for (const key of COIN_ORDER) {
    const coin = COINS[key]
    if (coin && coin.networks.some((n) => n.currency === currency)) return coin
  }
  // Недостижимо при согласованных типах, но валюта могла появиться
  // в API раньше, чем здесь: лучше показать тикер, чем упасть на
  // странице, где человек собирается платить.
  return { ticker: currency.toUpperCase(), label: currency, accent: '#7A7A7A',
           networks: [{ currency, name: currency.toUpperCase() }] }
}

/** Название сети для конкретной валюты API. */
export function networkOf(currency: Currency): string {
  const found = coinOf(currency).networks.find((n) => n.currency === currency)
  return found ? found.name : currency.toUpperCase()
}

/**
 * Монеты, которые реально можно выбрать: только те, под которые бекенд
 * отдал хотя бы одну сеть с адресами.
 */
export function coinsFor(available: Currency[]): CoinInfo[] {
  const set = new Set(available)
  const result: CoinInfo[] = []
  for (const key of COIN_ORDER) {
    const coin = COINS[key]
    if (!coin) continue
    const networks = coin.networks.filter((n) => set.has(n.currency))
    if (networks.length) result.push({ ...coin, networks })
  }
  return result
}


/**
 * Среднее время блока, минуты. Нужно не для точности, а чтобы
 * плательщик понимал масштаб ожидания: у биткоина это полчаса,
 * и без подсказки он решит, что страница зависла, и заплатит второй раз.
 */
export const BLOCK_MINUTES: Record<Currency, number> = {
  btc: 10,
  ltc: 2.5,
  xmr: 2,
  gram: 0.1,
  usdt_trc20: 0.05,
  usdt_ton: 0.1,
}
